import { createSlice } from '@reduxjs/toolkit';

// Initial state
const initialState = {
  settings: {
    language: 'vi',
    theme: 'light'
  },
  sellerAccounts: [],
  workers: [],
  users: [],
  roles: [],
  permissionMatrix: null,
  queueStatus: {},
  jobs: [],
  loading: false,
  error: null
};

// Admin settings slice
const adminSettingsSlice = createSlice({
  name: 'adminSettings',
  initialState,
  reducers: {
    setSettings: (state, action) => {
      state.settings = action.payload;
    },
    setSellerAccounts: (state, action) => {
      // Ensure we always set an array, even if the API returns an object
      state.sellerAccounts = Array.isArray(action.payload) ? action.payload : [];
    },
    addSellerAccountSuccess: (state, action) => {
      // Ensure sellerAccounts is an array before calling push
      if (!Array.isArray(state.sellerAccounts)) {
        state.sellerAccounts = [];
      }
      state.sellerAccounts.push(action.payload);
    },
    updateSellerAccountSuccess: (state, action) => {
      // Ensure sellerAccounts is an array
      if (!Array.isArray(state.sellerAccounts)) {
        state.sellerAccounts = [];
      } else {
        const index = state.sellerAccounts.findIndex(account => account.id === action.payload.id);
        if (index !== -1) {
          state.sellerAccounts[index] = action.payload;
        }
      }
    },
    deleteSellerAccountSuccess: (state, action) => {
      // Ensure sellerAccounts is an array
      if (!Array.isArray(state.sellerAccounts)) {
        state.sellerAccounts = [];
      } else {
        state.sellerAccounts = state.sellerAccounts.filter(account => account.id !== action.payload);
      }
    },
    setWorkers: (state, action) => {
      // Ensure we always set an array, even if the API returns an object
      state.workers = Array.isArray(action.payload) ? action.payload : [];
    },
    updateWorkerStatusSuccess: (state, action) => {
      if (!Array.isArray(state.workers)) {
        state.workers = [];
      } else {
        const index = state.workers.findIndex(worker => worker.id === action.payload.id);
        if (index !== -1) {
          state.workers[index] = { 
            ...state.workers[index], 
            ...action.payload
          };
        }
      }
    },
    setUsers: (state, action) => {
      // Ensure we always set an array, even if the API returns an object
      state.users = Array.isArray(action.payload) ? action.payload : [];
    },
    addUserSuccess: (state, action) => {
      // Ensure users is an array before calling push
      if (!Array.isArray(state.users)) {
        state.users = [];
      }
      state.users.push(action.payload);
    },
    updateUserSuccess: (state, action) => {
      // Ensure users is an array
      if (!Array.isArray(state.users)) {
        state.users = [];
      } else {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      }
    },
    deleteUserSuccess: (state, action) => {
      // Ensure users is an array
      if (!Array.isArray(state.users)) {
        state.users = [];
      } else {
        state.users = state.users.filter(user => user.id !== action.payload);
      }
    },
    setRoles: (state, action) => {
      // Ensure we always set an array, even if the API returns an object
      state.roles = Array.isArray(action.payload) ? action.payload : [];
    },
    addRoleSuccess: (state, action) => {
      // Ensure roles is an array before calling push
      if (!Array.isArray(state.roles)) {
        state.roles = [];
      }
      state.roles.push(action.payload);
    },
    updateRoleSuccess: (state, action) => {
      // Ensure roles is an array
      if (!Array.isArray(state.roles)) {
        state.roles = [];
      } else {
        const index = state.roles.findIndex(role => role.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      }
    },
    deleteRoleSuccess: (state, action) => {
      // Ensure roles is an array
      if (!Array.isArray(state.roles)) {
        state.roles = [];
      } else {
        state.roles = state.roles.filter(role => role.id !== action.payload);
      }
    },
    setPermissionMatrix: (state, action) => {
      state.permissionMatrix = action.payload;
    },
    setQueueStatus: (state, action) => {
      state.queueStatus = action.payload;
    },
    setJobs: (state, action) => {
      state.jobs = Array.isArray(action.payload) ? action.payload : [];
    },
    resetJobs: (state) => {
      state.jobs = [];
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  }
});

export const {
  setSettings,
  setSellerAccounts,
  addSellerAccountSuccess,
  updateSellerAccountSuccess,
  deleteSellerAccountSuccess,
  setWorkers,
  updateWorkerStatusSuccess,
  setUsers,
  addUserSuccess,
  updateUserSuccess,
  deleteUserSuccess,
  setRoles,
  addRoleSuccess,
  updateRoleSuccess,
  deleteRoleSuccess,
  setPermissionMatrix,
  setQueueStatus,
  setJobs,
  resetJobs,
  setLoading,
  setError
} = adminSettingsSlice.actions;

export default adminSettingsSlice.reducer;